/**
 * Connection-naming helpers used by the connection-creation surfaces.
 *
 * These are pure string helpers extracted from `ConnectionCreationPanel` so the
 * creation dialog, picker, tab, and agent-type view can share one
 * implementation instead of deep-importing the panel component.
 */

/**
 * Maximum length allowed for a connection name. Mirrors the limit enforced by
 * `validateConnectionName` in `manifestConnectionService`.
 */
export const CONNECTION_NAME_MAX_LENGTH = 80;

/**
 * Allowed connection-name shape: must start with a letter and contain only
 * letters, numbers, underscores, and hyphens (Azure resource naming rules).
 */
export const CONNECTION_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;

/**
 * Build a kebab-cased connection-name suggestion (e.g. `outlook-a1b2`) from a
 * connector name (or type), suffixed with a short random token to avoid
 * collisions with existing connections. Always returns a value that starts with
 * a letter and satisfies {@link CONNECTION_NAME_PATTERN}.
 *
 * Pass a stable `suffix` to keep the same token across re-derivations (e.g. when
 * the provider label changes but the underlying suggestion should not churn its
 * random token); omit it to generate a fresh one.
 */
export const generateSuggestedConnectionName = (
  connectorName: string | undefined,
  connectorType: string | undefined,
  suffix?: string,
): string => {
  const baseName = connectorName ?? connectorType ?? 'connection';
  let normalized = baseName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  if (normalized && !/^[a-z]/.test(normalized)) {
    normalized = `conn-${normalized}`;
  }
  if (!normalized) {
    normalized = 'connection';
  }
  const shortGuid = suffix ?? crypto.randomUUID().replace(/-/g, '').slice(0, 4);
  return `${normalized}-${shortGuid}`;
};

/**
 * Resolve the connector name to use when creating a connection.
 *
 * For Agent operations the existing connection's `displayName` includes the
 * selected model (e.g. "AzureOpenAI (gpt-4o)"), which would leak into the
 * suggested connection name as `azureopenai-gpt-4o-XXXX`. Prefer the stable
 * `agentModelType` so the suggestion is consistent across selections. Non-agent
 * operations prefer the connector display name. Both fall back to an explicit
 * fallback name and then the node type.
 */
export function resolveCreationConnectorName(opts: {
  isAgentOperation: boolean;
  agentModelType?: string | undefined;
  connectorDisplayName?: string | undefined;
  fallbackName?: string | undefined;
  nodeType?: string | undefined;
}): string | undefined {
  const { isAgentOperation, agentModelType, connectorDisplayName, fallbackName, nodeType } = opts;
  return isAgentOperation
    ? (agentModelType ?? fallbackName ?? nodeType)
    : (connectorDisplayName ?? fallbackName ?? nodeType);
}

/**
 * Normalize a connection name into its runtime reference key: lower-cased with
 * every run of whitespace collapsed to a single underscore. The runtime
 * resolves connections by this key, so the creation surfaces must derive it
 * identically (e.g. an agent connection and its underlying ApiHub OAuth
 * connection must produce the same key for names containing spaces/uppercase).
 */
export const normalizeConnectionReferenceKey = (name: string): string =>
  name.toLowerCase().replace(/\s+/g, '_');

/** Error message shown when a new connection name collides with an existing one. */
export const DUPLICATE_CONNECTION_NAME_ERROR =
  'A connection with this name already exists. Please choose a different name.';

/**
 * Whether `name` collides (case-insensitively) with an already-existing
 * connection name. Empty/undefined names are never treated as duplicates.
 */
export const isDuplicateConnectionName = (
  name: string | undefined,
  existingNames: string[],
): boolean => {
  if (!name) return false;
  const lower = name.toLowerCase();
  return existingNames.some((existing) => existing.toLowerCase() === lower);
};
